import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { applyLang, getCurrentLang } from '../utils/i18n';
import {
  campagnesApi, cotisationsApi,
  CAMPAGNE_CATEGORIES, CAT_LABEL, CAT_COLOR, STATUS_LABEL,
  fmtFCFA, MONTHLY_DUES_MIN,
} from '../services/caisseApi';
import './Caisse.css';

function campImage(c) {
  if (c.mainImageUrl) return c.mainImageUrl;
  const seed = c.mainImageSeed || `cb-caisse-${c.slug || c.id}`;
  return `https://picsum.photos/seed/${seed}/1600/1000`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

const Caisse = () => {
  const [campagnes,   setCampagnes]   = useState([]);
  const [cotisations, setCotisations] = useState([]);
  const [sumConfirmed, setSumConfirmed] = useState({ total: 0, count: 0 });
  const [filterCategory, setFilterCategory] = useState('all');

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.caisse-page .reveal').forEach((el) => io.observe(el));
    applyLang(getCurrentLang());
    return () => io.disconnect();
  }, [campagnes, cotisations]);

  useEffect(() => {
    let cancelled = false;
    campagnesApi.list({ category: filterCategory !== 'all' ? filterCategory : undefined, limit: 50 })
      .then((data) => { if (!cancelled) setCampagnes(data.items); })
      .catch(() => { if (!cancelled) setCampagnes([]); });
    return () => { cancelled = true; };
  }, [filterCategory]);

  useEffect(() => {
    let cancelled = false;
    cotisationsApi.list({ limit: 30 })
      .then((data) => {
        if (cancelled) return;
        setCotisations(data.items);
        setSumConfirmed(data.sumConfirmed || { total: 0, count: 0 });
      })
      .catch(() => { if (!cancelled) { setCotisations([]); setSumConfirmed({ total: 0, count: 0 }); } });
    return () => { cancelled = true; };
  }, []);

  const featured = useMemo(() => campagnes.filter((c) => c.featured && c.status === 'active'), [campagnes]);
  const active   = useMemo(() => campagnes.filter((c) => c.status === 'active' && !c.featured), [campagnes]);
  const reached  = useMemo(() => campagnes.filter((c) => c.status === 'atteinte' || c.status === 'cloturee'), [campagnes]);

  const totalGoal      = useMemo(() => campagnes.reduce((s, c) => s + (c.goal || 0), 0), [campagnes]);
  const totalRaised    = useMemo(() => campagnes.reduce((s, c) => s + (c.raised || 0), 0), [campagnes]);
  const activeCampCount = useMemo(() => campagnes.filter((c) => c.status === 'active').length, [campagnes]);

  return (
    <main className="caisse-page">
      {/* ============ HERO sobre, gradient sunset/dark ============ */}
      <header className="caisse-hero">
        <div className="caisse-hero-bg" />
        <div className="caisse-hero-grain" />

        <div className="caisse-hero-meta">
          <div><b>{fmtFCFA(totalRaised).replace(' FCFA','')}</b><span>FCFA collectés</span></div>
          <div><b>{sumConfirmed.count}</b><span>Contributeurs</span></div>
        </div>

        <div className="wrap caisse-hero-inner reveal">
          <span className="caisse-hero-eyebrow">Caisse du village · Transparence</span>
          <h1>
            Financer Cambérène,<br />
            <span className="ital">ensemble.</span>
          </h1>
          <p className="lede">
            École, mosquée, voirie, festival — chaque chantier du village a sa caisse.
            Vous contribuez selon vos moyens, nous publions chaque dépense. La transparence est totale.
          </p>

          <div className="caisse-hero-stats">
            <div className="caisse-hero-stat"><b>{activeCampCount}</b><span>Campagnes actives</span></div>
            <div className="caisse-hero-stat"><b>{Math.round(totalGoal/1_000_000)} M</b><span>FCFA d'objectif total</span></div>
            <div className="caisse-hero-stat"><b>{totalGoal > 0 ? Math.round((totalRaised/totalGoal)*100) : 0}%</b><span>Objectif atteint</span></div>
          </div>

          <div className="caisse-hero-actions">
            <a href="#campagnes" className="cs-btn primary">
              <span>Voir les campagnes</span><span className="arrow">→</span>
            </a>
            <a href="#contribuer" className="cs-btn outline">
              <span>Contribuer</span><span className="arrow">↓</span>
            </a>
          </div>
        </div>

        <div className="caisse-hero-scroll">
          <span>Faire défiler</span>
          <i />
        </div>
      </header>

      {/* ============ BANDEAU ADHÉSION MENSUELLE ============ */}
      <section className="caisse-dues-banner reveal">
        <div className="wrap">
          <div className="dues-card">
            <div className="dues-left">
              <span className="dues-eyebrow">Adhésion mensuelle obligatoire</span>
              <h3>{fmtFCFA(MONTHLY_DUES_MIN)} <span className="muted">ou plus</span></h3>
              <p>par membre, chaque mois, via <b>Wave</b>.</p>
            </div>
            <div className="dues-divider" aria-hidden="true" />
            <div className="dues-right">
              <p>
                Chaque membre du village contribue à la caisse commune.
                C'est ce qui permet de financer école, mosquée, voirie et événements collectifs.
                Pour suivre vos paiements, contactez la trésorerie.
              </p>
              <a href="#contribuer" className="cs-btn primary">
                <span>Comment payer via Wave</span><span className="arrow">↓</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CAMPAGNES VEDETTES (XL cards) ============ */}
      <section className="caisse-section" id="campagnes">
        <div className="wrap">
          <div className="caisse-section-head reveal">
            <div>
              <span className="eyebrow">Campagnes en vedette</span>
              <h2>Financer<br /><span className="ital">les priorités.</span></h2>
            </div>
            <p>
              Les chantiers que la communauté a décidé de pousser en priorité.
              Chaque cotisation s'ajoute en temps réel à la jauge.
            </p>
          </div>

          {featured.length > 0 && (
            <div className="feat-grid">
              {featured.map((c) => (
                <article key={c.id} className="feat-card reveal">
                  <div className="feat-photo">
                    <div className="img" style={{ backgroundImage: `url('${campImage(c)}')` }} />
                    <span className="cat" style={{ background: CAT_COLOR[c.category] }}>{CAT_LABEL[c.category]}</span>
                  </div>
                  <div className="feat-body">
                    <div className="meta">
                      <span className="status">⦿ {STATUS_LABEL[c.status]}</span>
                      {c.daysLeft != null && c.daysLeft > 0 && (<>· <span>{c.daysLeft} jours restants</span></>)}
                      {c.beneficiary && (<>· <span>{c.beneficiary}</span></>)}
                    </div>
                    <h3>{c.title}</h3>
                    <p>{c.shortDescription}</p>

                    <div className="progress-row">
                      <div className="progress-bar">
                        <div className="fill" style={{ width: `${c.progress}%`, background: CAT_COLOR[c.category] }} />
                      </div>
                      <span className="pct">{c.progress}%</span>
                    </div>
                    <div className="amounts">
                      <div><b>{fmtFCFA(c.raised)}</b><span>collectés</span></div>
                      <div><b>{fmtFCFA(c.goal)}</b><span>objectif</span></div>
                    </div>

                    <a href="#contribuer" className="cs-btn primary block">
                      <span>Soutenir cette campagne</span><span className="arrow">→</span>
                    </a>
                  </div>
                </article>
              ))}
            </div>
          )}

          {/* === FILTRES === */}
          <div className="caisse-filters reveal">
            {CAMPAGNE_CATEGORIES.map((c) => (
              <button
                key={c.id}
                className={`cs-pill ${filterCategory === c.id ? 'on' : ''}`}
                onClick={() => setFilterCategory(c.id)}
                style={filterCategory === c.id ? { background: c.color, borderColor: c.color, color: 'var(--bone)' } : {}}
              >{c.lbl}</button>
            ))}
          </div>

          {/* === Autres campagnes actives === */}
          {active.length > 0 && (
            <div className="cs-grid">
              {active.map((c) => (
                <article key={c.id} className="cs-card reveal">
                  <div className="cs-card-photo">
                    <div className="img" style={{ backgroundImage: `url('${campImage(c)}')` }} />
                    <span className="cat" style={{ background: CAT_COLOR[c.category] }}>{CAT_LABEL[c.category]}</span>
                  </div>
                  <div className="cs-card-body">
                    <h3>{c.title}</h3>
                    <p>{c.shortDescription}</p>
                    <div className="progress-bar small">
                      <div className="fill" style={{ width: `${c.progress}%`, background: CAT_COLOR[c.category] }} />
                    </div>
                    <div className="cs-card-foot">
                      <span><b>{fmtFCFA(c.raised)}</b> sur {fmtFCFA(c.goal)}</span>
                      <span className="pct">{c.progress}%</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}

          {/* === Atteintes / Clôturées === */}
          {reached.length > 0 && (
            <>
              <div className="caisse-section-head reveal" style={{ marginTop: 80 }}>
                <div>
                  <span className="eyebrow">Objectifs atteints</span>
                  <h2>Déjà <span className="ital">financé.</span></h2>
                </div>
                <p>Merci à tous les contributeurs. Voici ce que la caisse a permis d'accomplir.</p>
              </div>
              <div className="cs-grid">
                {reached.map((c) => (
                  <article key={c.id} className="cs-card cs-card-done reveal">
                    <div className="cs-card-photo">
                      <div className="img" style={{ backgroundImage: `url('${campImage(c)}')` }} />
                      <span className="cat done">✓ {STATUS_LABEL[c.status]}</span>
                    </div>
                    <div className="cs-card-body">
                      <h3>{c.title}</h3>
                      <p>{c.shortDescription}</p>
                      <div className="cs-card-foot">
                        <span><b>{fmtFCFA(c.raised)}</b> collectés</span>
                        <span className="pct done">100%</span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* ============ DERNIÈRES COTISATIONS (transparence) ============ */}
      <section className="caisse-section caisse-section-dark">
        <div className="wrap">
          <div className="caisse-section-head reveal">
            <div>
              <span className="eyebrow">Transparence · Cotisations</span>
              <h2>Qui contribue<br /><span className="ital">en ce moment.</span></h2>
            </div>
            <p>
              Toutes les cotisations confirmées, dans l'ordre. Les contributeurs qui le souhaitent restent anonymes.
              Total : <b>{fmtFCFA(sumConfirmed.total)}</b> sur <b>{sumConfirmed.count}</b> cotisations.
            </p>
          </div>

          {cotisations.length > 0 ? (
            <ul className="cot-list">
              {cotisations.map((c) => (
                <li key={c.id} className="cot-item">
                  <div className="cot-who">
                    <div className="avatar">{c.donorName ? c.donorName[0].toUpperCase() : '◆'}</div>
                    <div>
                      <b>{c.donorName || 'Contributeur anonyme'}</b>
                      <small>{fmtDate(c.date)}</small>
                    </div>
                  </div>
                  <div className="cot-amount">{fmtFCFA(c.amount)}</div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="cs-muted">Aucune cotisation enregistrée pour le moment.</p>
          )}
        </div>
      </section>

      {/* ============ COMMENT CONTRIBUER ============ */}
      <section className="caisse-section caisse-cta" id="contribuer">
        <div className="wrap reveal">
          <span className="eyebrow">Comment contribuer</span>
          <h2>4 façons<br /><span className="ital">de soutenir.</span></h2>
          <p className="lede">
            La caisse accepte les contributions en espèces à la mairie, par Mobile Money, par Wave ou par virement bancaire.
            Indiquez la campagne ciblée — ou laissez votre don à la libre affectation du conseil.
          </p>

          <div className="contrib-grid">
            <div className="contrib-card">
              <span className="num">01</span>
              <h3>Espèces · Mairie</h3>
              <p>Du lundi au vendredi, 8h–17h. Reçu officiel délivré sur place.</p>
              <span className="info">Mairie de Cambérène · Salle des paiements</span>
            </div>
            <div className="contrib-card">
              <span className="num">02</span>
              <h3>Mobile Money</h3>
              <p>Orange Money ou Free Money, à l'attention de la trésorerie du village.</p>
              <span className="info">+221 33 ••• ••• · Réf : CAISSE-CB</span>
            </div>
            <div className="contrib-card">
              <span className="num">03</span>
              <h3>Wave</h3>
              <p>QR code à scanner à la mairie ou directement au numéro dédié.</p>
              <span className="info">+221 77 ••• ••• · Caisse Cambérène</span>
            </div>
            <div className="contrib-card">
              <span className="num">04</span>
              <h3>Virement bancaire</h3>
              <p>Pour les contributions importantes ou la diaspora. RIB sur demande.</p>
              <span className="info">Compte CBAO · IBAN sur demande mairie</span>
            </div>
          </div>

          <div className="cta-foot">
            <Link to="/mairie" className="cs-btn outline-dark">
              <span>Contacter la trésorerie</span><span className="arrow">→</span>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Caisse;
